import {HubConnection, HubConnectionBuilder, HubConnectionState} from '@microsoft/signalr';
import Config from "../../config";
import {computed, observable} from "@legendapp/state";
import {GeneratedChatToken} from "_types/responses/generated-chat-token";
import {iso2date, iso2jsDate} from "_utils/date";
import {AuthService} from "_services/auth.service";
import {SendMessage} from "_types/requests/chat";
import {ChatMessage, NewMessage} from "_types/responses/chat";
import {StartOnline} from "_types/responses/Ws/start-online";
import {UserOnlineDto} from "_types/responses/Ws/online-users";
import {open} from "_pages/client/hearing/components/HearingStarted";
import {apiSwr, routes} from "_api";
import {HearingUpdatedDto} from "_types/responses/Ws/hearing-updated";
import {v4} from "uuid";
import {Sound, SoundService} from "_services/sound.service";
import {clientRoutes} from '_api/client-routes';
import {openAskConferenceSituation} from '_pages/client/conference/components/AskConferenceSituation';
import {$finishedByClient} from '_pages/client/conference/state';
import {openAskAttendanceSituation} from '_pages/client/desk/components/AskSituation';
import {enterVideoAttendance} from "_ui/Alerts";

const url = Config.API_URL + '/hearing-hub';

export enum ChatSessionFor {
    Attendance = 'attendance',
    Conference = 'conference',
    Hearing = 'hearing',
}

export const HearingHub = new class {
    private readonly _listenMap = new Map<string, (...args: any[]) => any>([
        ["Connected", this.onConnected.bind(this)],
        ["ReceiveMessage", this.onReceiveMessage.bind(this)],
        ["UsersOnline", this.onUsersOnline.bind(this)],
        ["HearingStarted", this.onHearingStarted.bind(this)],
        ["HearingFinished", this.onHearingFinished.bind(this)],
        ["HearingUpdated", this.onHearingUpdated.bind(this)],
        ["NewParticipant", this.onNewParticipant.bind(this)],
        ["VideoLink", this.VideoStarted.bind(this)],
    ]);

    private _hub: HubConnection | null = null;

    private _currentChatId: string | null = null;

    private _$isConnected = observable(false);
    private _$token = observable<string | undefined>(undefined);
    private lastV4 = '';

    public $myInfo = computed(() => {
        return this.$usersOnline.$
            .find(u => this.isForClient
                ? u.clientServed?.id == this.clientServedId
                : u.user?.id == AuthService.currentUser?.id
            );
    });

    public clientServedId = '';

    public closing = false;
    public _isForClient = false;
    public onlyView = false;
    public $messages = observable<ChatMessage[]>([]);
    public $usersOnline = observable<UserOnlineDto[]>([]);
    public $sending = observable<boolean>(false);
    public onlineSent = false;
    public entityId = '';
    public $videoLink = observable<string | null>(null);

    public for = ChatSessionFor.Hearing;

    public get isForClient() {
        return this._isForClient;
    }

    public forClient() {
        this._isForClient = true;
        return this;
    }

    public forAdmin() {
        this._isForClient = false;
        return this;
    }

    public get $token() {
        return this._$token;
    }

    public get $isConnected() {
        return this._$isConnected;
    }

    public get currentChatId() {
        return this._currentChatId;
    }

    public set currentChatId(value: string | null) {
        this._currentChatId = value;
    }

    constructor() {
        this._$isConnected.onChange(() => {
            if (this.$token.get()) {
                this.startOnline();
            }
        });

        setInterval(this.checkForOnlineSent.bind(this), 1000);
    }

    private checkForOnlineSent() {
        if (this._$isConnected.get() && !this.onlineSent) {
            this.startOnline();
        }
    }

    public storeGeneratedToken(chatSessionId: string, entityId: string, value: GeneratedChatToken, startOnline = true) {
        const key = this.storageKey(chatSessionId, entityId);

        localStorage.setItem(
            key,
            JSON.stringify(value)
        );

        if (this.isForClient) {
            this.clientServedId = value.clientServedId;
        }
        this.entityId = entityId;
        this._$token.set(value.token);

        startOnline && this.startOnline();
    }

    public loadGeneratedToken(chatSessionId: string, entityId: string) {
        try {
            const key = this.storageKey(chatSessionId, entityId);
            const token = JSON.parse(localStorage.getItem(key) || '{}') as GeneratedChatToken;

            this.entityId = entityId;
            if (token && token.token) {
                const expiration = iso2jsDate(token.expirationDate);
                if (!expiration || expiration < new Date()) {
                    localStorage.removeItem(key);
                    this._$token.set(undefined);
                    return false;
                }
                this._$token.set(token.token);
                if (this.isForClient) {
                    this.clientServedId = token.clientServedId;
                }

                this.startOnline();
                return true;
            }
            return false;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    private storageKey(chatSessionId: string, entityId: string) {
        return this.isForClient
            ? `clientChat_${chatSessionId}_for_${entityId}`
            : `chat_${chatSessionId}_for_${entityId}_user-client_${AuthService.currentUser!.id}`;
    }

    public async connect(usedFor = ChatSessionFor.Hearing) {
        this.for = usedFor;
        this.closing = false;
        this.onlineSent = false;
        this.$videoLink.set(null);

        if (this._hub || this.onlyView) {
            return;
        }

        this._hub = new HubConnectionBuilder()
            .withUrl(url)
            .withAutomaticReconnect([0, 2000, 10000, 30000])
            .build();

        this._listenMap.forEach((handler, event) => {
            this._hub?.on(event, handler);

        });

        await this._hub.start();
    }

    public startOnline() {
        const startOnline = {
            chatSessionId: this.currentChatId,
            token: this._$token.get(),
        } as StartOnline;

        if (this.isForClient) {
            if (!this.clientServedId) {
                return;
            }

            startOnline.clientServedId = this.clientServedId;
        } else {
            startOnline.userId = AuthService.currentUser!.id;
        }

        if (this._$isConnected.get()) {
            this.invoke('Online', startOnline);
            this.onlineSent = true;
        }
    }

    public sendMessage(message: string) {
        this.$sending.set(true);
        this.lastV4 = v4();

        const newMessage = {
            uuid: this.lastV4,
            token: this.$token.get(),
            content: message,
            chatSessionId: this.currentChatId,
        } as SendMessage;

        if (this.isForClient) {
            newMessage.clientServedId = this.clientServedId;
        } else {
            newMessage.userId = AuthService.currentUser!.id;
        }

        if (!this._hub) {
            return;
        }

        this.invoke('SendMessage', newMessage);
    }

    public disconnect() {
        if (this.closing) {
            return;
        }
        this.closing = true;
        if (this._hub) {
            this._hub?.stop();
            this._hub = null;
            this._$isConnected.set(false);
        }
    }


    private invoke(event: string, data?: any) {
        if (this._hub && !this.closing) {
            if (this._hub.state === HubConnectionState.Connected) {
                this._hub?.invoke(event, data);
            } else if ([HubConnectionState.Connecting, HubConnectionState.Reconnecting].includes(this._hub.state)) {
                setTimeout(() => {
                    this.invoke(event, data);
                }, 1000);
            }
        }
    }

    private onReceiveMessage(data: NewMessage) {

        if (data.clientServedId == this.clientServedId || (data.userId == AuthService?.currentUser?.id && !HearingHub.isForClient)) {
            this.$sending.set(false);
        } else {
            SoundService.play(Sound.Message);
        }

        this.$messages.set([
                ...this.$messages.get(),
                {
                    ...data,
                    variant: 'primary',
                    icon: 'fas fa-user',
                } as ChatMessage
            ]
                .sort((a, b) => iso2date(a.creationDate)!.toMillis() < iso2date(b.creationDate)!.toMillis() ? -1 : 1)
        );
    }

    public async VideoStarted(link: string) {
        if(!this.isForClient){
            return;
        }

        this.$videoLink.set(link);

        if ((await enterVideoAttendance()).isConfirmed) {
            window.open(link, '_BLANK');

        }
    }

    private onHearingStarted(link: string) {
        if (this.for == ChatSessionFor.Hearing) {
            open(link);
            return;
        }

        if (this.for == ChatSessionFor.Conference) {
            setTimeout(() => {
                apiSwr.invalidateQueries({queryKey: [...clientRoutes.conference(this.entityId!).show]});
            }, 1000);
            return;
        }
    }

    private onHearingFinished(link: string) {
        if (this.for == ChatSessionFor.Hearing) {
            apiSwr.invalidateQueries({queryKey: [...clientRoutes.hearing(this.entityId).show]});
            return;
        }

        if (this.for == ChatSessionFor.Conference) {
            if (this.isForClient) {
                if (!$finishedByClient.$) {
                    openAskConferenceSituation();
                }
                apiSwr.invalidateQueries({queryKey: [...clientRoutes.conference(this.entityId!).show]});
            } else {
                apiSwr.invalidateQueries({queryKey: [...routes.conference(this.entityId!).show]});
            }
            return;
        }

        // if(this.isForClient){
        //     openAskAttendanceSituation();
        //     apiSwr.invalidateQueries({ queryKey: [...clientRoutes.attendance(this.entityId!).show] });
        // }
    }

    private onNewParticipant() {
        apiSwr.invalidateQueries({queryKey: [...routes.chat.messages(this.currentChatId!).participants]});
    }

    private async onHearingUpdated(data: HearingUpdatedDto) {
        await apiSwr.invalidateQueries({queryKey: [...routes.hearing(data.hearingId).show]});
        await apiSwr.invalidateQueries({queryKey: [...routes.hearings.event(data.eventId).show]});
    }

    private onUsersOnline(users: UserOnlineDto[]) {
        setTimeout(() => this.$usersOnline.set(users || []));
    }

    private onConnected() {
        this._$isConnected.set(true);
    }
};
